import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { MaterialsStockReportService } from "services/materials-stock-report";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  itemName: "",
  itemCode: "",
  unit: "",
  totalQuantity: "",
  quantityRemainForUse: "",
  quantityReturnedToStock: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

export default function MaterialsStockReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    usageDepartment: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await MaterialsStockReportService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          usageDepartment: data.usageDepartment || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          itemCode: row.itemCode || "",
          unit: row.unit || "",
          totalQuantity: formatQtyOutput(row.totalQuantity),
          quantityRemainForUse: formatQtyOutput(row.quantityRemainForUse),
          quantityReturnedToStock: formatQtyOutput(row.quantityReturnedToStock),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kê vật tư còn lại");
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        totalQuantity: acc.totalQuantity + parseQty(row.totalQuantity),
        quantityRemainForUse: acc.quantityRemainForUse + parseQty(row.quantityRemainForUse),
        quantityReturnedToStock:
          acc.quantityReturnedToStock + parseQty(row.quantityReturnedToStock),
      }),
      { totalQuantity: 0, quantityRemainForUse: 0, quantityReturnedToStock: 0 }
    );
  }, [rows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: ["totalQuantity", "quantityRemainForUse", "quantityReturnedToStock"].includes(field)
          ? formatQtyInput(value)
          : value,
      };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const usageDepartment = String(header.usageDepartment || "").trim();

    const documentDate = validateDate(documentDateParts, "Ngày lập bảng kê");

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số bảng kê không được để trống");
    if (!usageDepartment) throw new Error("Bộ phận sử dụng không được để trống");

    const normalizedRows = rows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        itemCode: String(row.itemCode || "").trim(),
        unit: String(row.unit || "").trim(),
        totalQuantity: parseQty(row.totalQuantity),
        quantityRemainForUse: parseQty(row.quantityRemainForUse),
        quantityReturnedToStock: parseQty(row.quantityReturnedToStock),
      }))
      .filter((row) => {
        const hasQty = row.totalQuantity > 0 || row.quantityRemainForUse > 0 || row.quantityReturnedToStock > 0;
        return row.itemName || row.itemCode || row.unit || hasQty;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng kê vật tư còn lại phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) {
        throw new Error(`Dòng ${index + 1}: Tên, nhãn hiệu, quy cách không được để trống`);
      }

      const delta = Math.abs(row.totalQuantity - (row.quantityRemainForUse + row.quantityReturnedToStock));
      if (delta > 0.0001) {
        throw new Error(`Dòng ${index + 1}: Tổng phải bằng Để lại sử dụng + Nhập lại kho`);
      }
    });

    return {
      unit,
      department,
      documentDate,
      documentNumber,
      usageDepartment,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await MaterialsStockReportService.create(payload);
        toast.success("Tạo bảng kê vật tư còn lại thành công");
      } else {
        await MaterialsStockReportService.update(id, payload);
        toast.success("Cập nhật bảng kê vật tư còn lại thành công");
      }
      navigation("/templates/materials-stock-report");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
      },
      rows: rows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 980 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 04 - VT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 28, textTransform: "uppercase" }}>BẢNG KÊ CHI TIẾT VẬT TƯ CÒN LẠI CUỐI KỲ</div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />
                    {" "}tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />
                    {" "}năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField("year", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 8 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                    value={header.documentNumber}
                    onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                  />
                </div>

                <div style={{ marginTop: 6 }}>
                  <b>Bộ phận sử dụng:</b>{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 300 }}
                    value={header.usageDepartment}
                    onChange={(event) => setHeaderField("usageDepartment", event.target.value)}
                  />
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 8 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ ...s.th, width: 60 }}>Số TT</th>
                        <th rowSpan={2} style={{ ...s.th, width: 320 }}>Tên, nhãn hiệu, quy cách, phẩm chất vật tư</th>
                        <th rowSpan={2} style={{ ...s.th, width: 120 }}>Mã số</th>
                        <th rowSpan={2} style={{ ...s.th, width: 120 }}>Đơn vị tính</th>
                        <th colSpan={3} style={{ ...s.th, width: 300 }}>Số lượng</th>
                        {!containView ? <th rowSpan={2} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>Tổng</th>
                        <th style={s.th}>Để lại sử dụng</th>
                        <th style={s.th}>Nhập lại kho</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        {!containView ? <th style={s.th}>#</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`materials-stock-row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.itemName}
                              onChange={(event) => setRowField(index, "itemName", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.itemCode}
                              onChange={(event) => setRowField(index, "itemCode", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.unit}
                              onChange={(event) => setRowField(index, "unit", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.totalQuantity}
                              onChange={(event) => setRowField(index, "totalQuantity", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityRemainForUse}
                              onChange={(event) => setRowField(index, "quantityRemainForUse", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityReturnedToStock}
                              onChange={(event) => setRowField(index, "quantityReturnedToStock", event.target.value)}
                            />
                          </td>
                          {!containView ? (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => removeRow(index)}
                                style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                              >
                                x
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={4} style={{ ...s.th, textAlign: "center" }}>
                          Cộng
                        </td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.totalQuantity)}</td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.quantityRemainForUse)}</td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.quantityReturnedToStock)}</td>
                        {!containView ? <td style={s.th}></td> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                  <SoftButton onClick={() => navigation("/templates/materials-stock-report")}>Trở về</SoftButton>

                  <SoftBox display="flex" gap={1}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
